import { useEffect } from 'react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';

/**
 * Cookie Policy — the per-item inventory that the Privacy Notice points at.
 * The Privacy Notice explains categories, legal bases, and rights at the
 * level of processing; this page lists the specific cookies and storage
 * items actually used.
 *
 * VERSIONING: COOKIE_POLICY_VERSION ties to the Privacy Notice version
 * because changes here typically reflect Privacy Notice changes.
 */
export const COOKIE_POLICY_VERSION = '2026-v2';
export const COOKIE_POLICY_LAST_UPDATED = '2026-06-26';

export default function CookiePolicyPage() {
  useEffect(() => {
    applyPageMeta(PAGE_META.cookies);
  }, []);

  return (
    <article className="mx-auto max-w-3xl px-6 py-16 text-white/85">
      <header className="border-b border-white/15 pb-6">
        <h1 className="font-display text-3xl uppercase tracking-widest text-brand-redAccent">
          Cookie Policy
        </h1>
        <p className="mt-3 text-sm text-white/60">
          Version {COOKIE_POLICY_VERSION} &middot; Last updated{' '}
          {COOKIE_POLICY_LAST_UPDATED}
        </p>
      </header>

      <div className="mt-8 space-y-12 leading-relaxed">
        <Section id="what-are-cookies" title="1. What this page covers">
          <p>
            This page lists every cookie and similar technologies used on this
            site, what each one does, how long each remains on your device,
            which consent category each belongs to, and how you can manage your
            preferences. For the broader picture — how we collect and use your
            personal information, who we may share it with, and your privacy
            rights — see the{' '}
            <a href="/privacy" className="text-brand-redAccent underline-offset-2 hover:underline">
              Privacy Notice
            </a>
            .
          </p>
          <p>
            "Cookies" in this notice includes traditional HTTP cookies as well
            as the related browser-storage technologies, such as localStorage
            (used to remember your consent decision and a few preferences
            across visits) and sessionStorage (used to cache short-lived values
            within a single tab session), that help the site function and
            remember your preferences.
          </p>
        </Section>

        <Section id="categories" title="2. The three categories">
          <p>
            We group cookies and similar technologies into three categories.
            You can manage your preferences for Analytics and Marketing cookies
            through the Privacy Settings page. It also lets you control the two
            opt-in categories. Strictly-necessary category is on for everyone
            because the site cannot function without it.
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Strictly necessary.</strong> Used to keep you signed
              in, to remember the consent decision you made, to determine
              your country for contest eligibility, and to make basic
              navigation work. Always on. Without these the site cannot
              function.
            </li>
            <li>
              <strong>Analytics.</strong> Used to measure how the site is
              used — which pages, which features, what is working and what
              is not — so we can improve it. Off by default for visitors in
              the EEA, UK, and Switzerland; on by default elsewhere with the
              ability to opt out at any time. Cookieless analytics
              (Plausible) runs regardless because it sets no device storage
              and is anonymized at the server side.
            </li>
            <li>
              <strong>Marketing / advertising.</strong> Used by State Farm
              for advertising-measurement purposes via the DEEL tag.{' '}
              <strong>DEEL is loaded only for visitors detected as being in
              the United States.</strong> If you are outside the U.S.
              (including the EEA, UK, Switzerland, Canada, or anywhere else)
              the DEEL script is not loaded at all, regardless of consent.
              U.S. visitors can opt out at any time on the Privacy Settings
              page. The marketing-storage toggle on that page also controls
              any future advertising tags Google Tag Manager may load
              consistently for all regions.
            </li>
          </ul>
        </Section>

        <Section id="inventory" title="3. Inventory">
          <p>
            The table below lists the cookies and similar technologies used by
            this site. "Category" tells you which Privacy Settings toggle
            controls it.
          </p>

          <div className="mt-4 overflow-x-auto rounded-lg border border-white/10">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/70">
                <tr>
                  <th scope="col" className="px-3 py-2">Name</th>
                  <th scope="col" className="px-3 py-2">Type</th>
                  <th scope="col" className="px-3 py-2">Set by</th>
                  <th scope="col" className="px-3 py-2">Purpose</th>
                  <th scope="col" className="px-3 py-2">Duration</th>
                  <th scope="col" className="px-3 py-2">Category</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 text-white/80">
                <Row
                  name="gh_session"
                  type="HTTP cookie"
                  setBy="This site"
                  purpose="Tells the site you are signed in so it can attempt to refresh your session on boot."
                  duration="30 days; cleared on sign-out"
                  category="Strictly necessary"
                />
                <Row
                  name="refreshToken"
                  type="HTTP cookie (HttpOnly, Secure in production)"
                  setBy="This site"
                  purpose="Refresh token used to keep your session alive. Rotated on every refresh; cannot be read by JavaScript."
                  duration="30 days; cleared on sign-out"
                  category="Strictly necessary"
                />
                <Row
                  name="gh_consent"
                  type="localStorage"
                  setBy="This site"
                  purpose="Remembers your choices on this page (analytics on/off, marketing on/off) and the version of the notice you saw."
                  duration="Until you clear your browser storage"
                  category="Strictly necessary"
                />
                <Row
                  name="gh_consent_sid"
                  type="sessionStorage"
                  setBy="This site"
                  purpose="Random ID tying multiple consent events from the same browser tab together for audit purposes."
                  duration="Tab session"
                  category="Strictly necessary"
                />
                <Row
                  name="gh_viewer_country"
                  type="sessionStorage"
                  setBy="This site"
                  purpose="Caches your approximate country (ISO code only) so the eligibility notice on the Official Rules page does not flicker on navigation."
                  duration="Tab session"
                  category="Strictly necessary"
                />
                <Row
                  name="gh_signup_started_at, gh_weekly_consent_started_at"
                  type="sessionStorage"
                  setBy="This site"
                  purpose="Records when you started the registration or weekly-consent flow so we can measure how long each step takes."
                  duration="Tab session; cleared on submit"
                  category="Strictly necessary"
                />
                <Row
                  name="plausible_ignore"
                  type="localStorage"
                  setBy="This site (Plausible opt-out)"
                  purpose="If you toggle 'Don't count my visit' on the Privacy Settings page, this value blocks Plausible from loading on future visits."
                  duration="Until you toggle it off"
                  category="Analytics (opt-out only)"
                />
                <Row
                  name="_ga, _ga_*, _gid, _gat, etc."
                  type="HTTP cookies"
                  setBy="Google Analytics via Google Tag Manager"
                  purpose="Standard Google Analytics identifiers and rate-limiting flags. Set by default for US / non-GDPR visitors (opt out any time); set only after opt-in for EEA / UK / Swiss visitors."
                  duration="Up to 2 years (Google policy)"
                  category="Analytics"
                />
                <Row
                  name="DEEL cookies"
                  type="HTTP cookies"
                  setBy="State Farm DEEL tag (invocation.deel.c1.statefarm) — US visitors only"
                  purpose="State Farm advertising and measurement. Loaded only for visitors detected as being in the United States; not loaded at all for visitors outside the U.S., regardless of consent. U.S. visitors can opt out at any time on the Privacy Settings page."
                  duration="Per State Farm policy"
                  category="Marketing / advertising (US only)"
                />
                <Row
                  name="Plausible"
                  type="(no cookies)"
                  setBy="This site (self-hosted Plausible)"
                  purpose="Cookieless analytics. Deduplicates visits server-side using a hash of IP and user agent that rotates every 24 hours; nothing is stored on your device."
                  duration="n/a"
                  category="Analytics (cookieless; exempt under most cookie laws)"
                />
                <Row
                  name="STATEFARM_ENSIGHTEN_PRIVACY_Analytics"
                  type="HTTP cookie"
                  setBy="State Farm Do Not Sell or Share preference center (statefarm.com)"
                  purpose="Cross-domain opt-out signal. Set on the gamerhood domain by State Farm when you save preferences on their Do Not Sell or Share page. For US visitors who have not made an explicit choice on our Privacy Settings page, we read this cookie at boot and apply its value to our Google Analytics storage state (denied if the cookie is '0')."
                  duration="~1 year"
                  category="Strictly necessary (cross-domain opt-out signal)"
                />
                <Row
                  name="STATEFARM_ENSIGHTEN_PRIVACY_Marketing"
                  type="HTTP cookie"
                  setBy="State Farm Do Not Sell or Share preference center (statefarm.com)"
                  purpose="Cross-domain opt-out signal for advertising. The DEEL script reads this cookie on its own behalf when it runs, to suppress its tracking calls. Our code does NOT read this cookie."
                  duration="~1 year"
                  category="Strictly necessary (cross-domain opt-out signal)"
                />
                <Row
                  name="STATEFARM_ENSIGHTEN_PRIVACY_Performance, _Social"
                  type="HTTP cookies"
                  setBy="State Farm Do Not Sell or Share preference center (statefarm.com)"
                  purpose="Cross-domain opt-out signals for the corresponding categories on State Farm's preference page. Neither maps to anything this site currently loads; our code does not read them."
                  duration="~1 year"
                  category="Strictly necessary (cross-domain opt-out signal)"
                />
              </tbody>
            </table>
          </div>

          <p className="text-xs text-white/60">
            The list above is current as of {COOKIE_POLICY_LAST_UPDATED}. The
            specific identifiers a third party sets (Google, State Farm) can
            change without notice to us; the categories and the gating
            behavior they fall under remain as described.
          </p>
        </Section>

        <Section id="manage" title="4. Managing your choices">
          <p>
            You can change your analytics and marketing choices at any time on
            the{' '}
            <a href="/privacy-settings" className="text-brand-redAccent underline-offset-2 hover:underline">
              Privacy Settings
            </a>{' '}
            page. Changes apply immediately — turning off marketing storage
            removes the State Farm DEEL tag from the page without a reload,
            and turning off analytics storage tells Google Tag Manager to
            stop reading and writing analytics cookies on subsequent events.
          </p>
          <p>
            You can also block or delete cookies from your browser settings.
            Doing so may break parts of the site that depend on the strictly
            necessary cookies — for example, you will not be able to stay
            signed in.
          </p>
          <p>
            <strong>Global Privacy Control.</strong> If your browser sends
            the Global Privacy Control (GPC) signal, we treat it as an
            opt-out of marketing storage and as a request not to share
            personal information for advertising purposes. The consent
            banner does not appear when GPC is detected.
          </p>
        </Section>

        <Section id="changes" title="5. Updates">
          <p>
            We update this page when we add, remove, or change a cookie or
            storage item. The Last updated date at the top reflects the most
            recent change. Where required by law, we will notify you of
            material changes that significantly affect your rights and
            obligations. We may ask you to review your privacy settings and
            preferences as needed.
          </p>
        </Section>
      </div>
    </article>
  );
}

type SectionProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-heading text-lg uppercase tracking-wider text-white">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-[15px] text-white/80">{children}</div>
    </section>
  );
}

type RowProps = {
  name: string;
  type: string;
  setBy: string;
  purpose: string;
  duration: string;
  category: string;
};

function Row({ name, type, setBy, purpose, duration, category }: RowProps) {
  return (
    <tr className="align-top">
      <td className="px-3 py-3 font-mono text-xs">{name}</td>
      <td className="px-3 py-3 text-xs">{type}</td>
      <td className="px-3 py-3 text-xs">{setBy}</td>
      <td className="px-3 py-3 text-xs">{purpose}</td>
      <td className="px-3 py-3 text-xs">{duration}</td>
      <td className="px-3 py-3 text-xs font-semibold uppercase tracking-wide">
        {category}
      </td>
    </tr>
  );
}
